import nacl from 'tweetnacl'
import { encodeBase64 } from 'tweetnacl-util'

/**
 * Device-local storage of the E2E identity keypair (X25519).
 *
 * The SECRET key never leaves this device — it lives only in IndexedDB and is
 * never uploaded. Model: no backup (like Telegram secret chats). A new device
 * generates a fresh keypair, so old secret-chat history there is unreadable.
 */

const DB_NAME = 'rooms-e2e'
// Версия 2: хранилище последних виденных ключей собеседников (peers) — по нему
// замечаем смену ключа устройства собеседника.
const DB_VERSION = 2
const STORE = 'identity'
export const PEERS_STORE = 'peers'
const IDENTITY_KEY = 'self'

export interface IdentityKeyPair {
  publicKey: Uint8Array
  secretKey: Uint8Array
}

export function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE)
      }
      if (!db.objectStoreNames.contains(PEERS_STORE)) {
        db.createObjectStore(PEERS_STORE)
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbGet<T>(key: string): Promise<T | undefined> {
  const db = await openDB()
  return new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(key)
    req.onsuccess = () => resolve(req.result as T | undefined)
    req.onerror = () => reject(req.error)
  })
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDB()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

/** Read the stored identity keypair, or null if this device has none yet. */
export async function getIdentity(): Promise<IdentityKeyPair | null> {
  const stored = await idbGet<{ publicKey: Uint8Array; secretKey: Uint8Array }>(IDENTITY_KEY)
  if (!stored || !stored.secretKey || !stored.publicKey) return null
  // IndexedDB preserves Uint8Array, but be defensive across browsers.
  return {
    publicKey: new Uint8Array(stored.publicKey),
    secretKey: new Uint8Array(stored.secretKey),
  }
}

/** Return the device identity keypair, generating and persisting one if absent. */
export async function getOrCreateIdentity(): Promise<IdentityKeyPair> {
  const existing = await getIdentity()
  if (existing) return existing

  const kp = nacl.box.keyPair()
  await idbSet(IDENTITY_KEY, { publicKey: kp.publicKey, secretKey: kp.secretKey })
  return { publicKey: kp.publicKey, secretKey: kp.secretKey }
}

/** Base64 of this device's public identity key (safe to upload/share). */
export async function getPublicKeyBase64(): Promise<string> {
  const id = await getOrCreateIdentity()
  return encodeBase64(id.publicKey)
}

/** Wipe the device identity (e.g. on logout). Renders local secret history unreadable. */
export async function clearIdentity(): Promise<void> {
  const db = await openDB()
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(IDENTITY_KEY)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}
